import { CloseCircleGreyIcon } from "assets";
import { ModalPortal, MODAL_LAYER } from "components/modal-portal";
import { formatCurrency } from "utils/pricing";
import type { CheckoutTopupData } from "./types";

interface CreditCheckoutModalProps {
  data: CheckoutTopupData;
  isProcessing: boolean;
  onProceed: () => void;
  onClose: () => void;
}

/** Order summary shown before handing the user off to the payment provider. */
const CreditCheckoutModal = ({ data, isProcessing, onProceed, onClose }: CreditCheckoutModalProps) => {
  const { credits, totalPrice, costPerCredit, currency, validityDays } = data;

  return (
    <ModalPortal>
      <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: MODAL_LAYER.checkout }}>
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />

        {/* Modal */}
        <div className="pt-4 relative bg-white rounded-2xl max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 z-10 hover:opacity-80 transition-opacity"
          >
            <CloseCircleGreyIcon className="w-8 h-8" />
          </button>

          {/* Header */}
          <div className="text-center pt-8 pb-4 px-6">
            <h2 className="text-2xl font-bold text-[#012169]">Credit Top Up</h2>
            <p className="text-[#667085] text-sm mt-1">
              You are about to purchase additional credits
            </p>
          </div>

          {/* Body */}
          <div className="px-6 pb-6">
            <div className="border border-[#EAECF0] rounded-xl p-5 bg-[#F9FAFB]">
              <h3 className="text-2xl font-bold text-[#101828]">Credit Top Up</h3>
              <p className="text-sm text-[#175CD3] font-medium mt-1">One-time purchase</p>

              <ul className="mt-5 space-y-2.5">
                {[
                  `Credits: ${credits.toLocaleString()}`,
                  `Cost per credit: ${formatCurrency(costPerCredit, currency)}`,
                  `Valid for ${validityDays} days from purchase`,
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2 text-sm text-[#344054]">
                    <svg
                      className="w-5 h-5 text-[#17B26A] flex-shrink-0 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {line}
                  </li>
                ))}
              </ul>

              <hr className="my-5 border-[#EAECF0]" />

              <div className="space-y-2">
                <div className="flex justify-between text-sm text-[#344054]">
                  <span>{credits.toLocaleString()} credits</span>
                  <span>{formatCurrency(totalPrice, currency)}</span>
                </div>
                <div className="flex justify-between text-sm text-[#344054]">
                  <span>Estimated tax</span>
                  <span>{formatCurrency(0, currency)}</span>
                </div>
                <div className="flex justify-between text-base font-bold text-[#101828] pt-1">
                  <span>Total</span>
                  <span>{formatCurrency(totalPrice, currency)}</span>
                </div>
              </div>
            </div>

            <button
              onClick={onProceed}
              disabled={isProcessing}
              className="w-full py-4 mt-6 bg-[#012169] text-white font-medium rounded-lg
                         hover:bg-[#011557] transition-colors disabled:opacity-60
                         disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  Processing...
                </>
              ) : (
                "Proceed to Checkout"
              )}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export { CreditCheckoutModal };
